import { useEffect, type ReactElement } from 'react';
import { StyleSheet, View } from 'react-native';
import { captureScreen } from 'react-native-view-shot';

import { dropQueuedFeedbackNavigation } from './feedbackCapture';
import {
  decodedBase64Bytes,
  FeedbackFileError,
  FeedbackFilePayload,
  feedbackFilePayload,
} from './feedbackFile';
import { showToast } from '../../utils/toast';

const CAPTURE_FAILED = 'Could not capture the screen.';
const CAPTURE_TIMEOUT_MS = 8000;

/**
 * The feedback dialog is a native modal window. The screenshot waits for that
 * window to leave, or the picture is the form. There is no signal for when
 * the window is gone.
 */
const DIALOG_LEAVE_MS = 400;

interface DeviceScreenCaptureProps {
  onDone: (file: FeedbackFilePayload) => void;
  onDismiss: () => void;
}

function pngText(shot: string): string {
  const marker = 'base64,';
  const markerAt = shot.toLowerCase().indexOf(marker);
  const payload = markerAt === -1 ? shot : shot.slice(markerAt + marker.length);
  return payload.replace(/\s/g, '');
}

/**
 * Photographs the app window and attaches it. No crop chrome.
 * iOS snapshots the key window. Android snapshots the app content.
 * The system status icons live outside that window.
 */
export function DeviceScreenCapture({
  onDone,
  onDismiss,
}: DeviceScreenCaptureProps): ReactElement {
  useEffect(() => {
    let cancelled = false;
    let leaveTimer: ReturnType<typeof setTimeout> | undefined;
    let shotTimer: ReturnType<typeof setTimeout> | undefined;

    const run = async () => {
      let file: FeedbackFilePayload;
      try {
        await new Promise<void>(resolve => {
          if (process.env.JEST_WORKER_ID !== undefined) {
            resolve();
            return;
          }
          leaveTimer = setTimeout(resolve, DIALOG_LEAVE_MS);
        });
        if (cancelled) {
          return;
        }
        const shot = pngText(
          await new Promise<string>((resolve, reject) => {
            shotTimer = setTimeout(() => {
              reject(new Error('capture-timeout'));
            }, CAPTURE_TIMEOUT_MS);
            captureScreen({ format: 'png', result: 'base64' }).then(
              value => {
                clearTimeout(shotTimer);
                resolve(value);
              },
              error => {
                clearTimeout(shotTimer);
                reject(
                  error instanceof Error ? error : new Error('capture-failed'),
                );
              },
            );
          }),
        );
        if (cancelled) {
          return;
        }
        file = feedbackFilePayload({
          fileName: 'screen.png',
          mimeType: 'image/png',
          fileSizeBytes: decodedBase64Bytes(shot),
          fileData: shot,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }
        dropQueuedFeedbackNavigation();
        showToast.error(
          error instanceof FeedbackFileError ? error.message : CAPTURE_FAILED,
        );
        onDismiss();
        return;
      }
      if (cancelled) {
        return;
      }
      dropQueuedFeedbackNavigation();
      onDone(file);
    };

    run().catch(() => undefined);

    return () => {
      cancelled = true;
      clearTimeout(leaveTimer);
      clearTimeout(shotTimer);
    };
  }, [onDone, onDismiss]);

  return (
    <View
      testID="feedback-device-capture"
      pointerEvents="auto"
      collapsable={false}
      style={styles.shield}
    />
  );
}

const styles = StyleSheet.create({
  shield: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1000,
    elevation: 1000,
    backgroundColor: 'transparent',
  },
});
